'use client'

import {useEffect, useId, useMemo, useRef, useState, useTransition} from 'react'
import {usePathname, useRouter} from 'next/navigation'
import {useTranslations} from 'next-intl'
import {Button, Checkbox, Input, Select} from '@/design-system'
import {regionKey} from '@/components/universities/labels'
import {CATALOGUE_COUNTRY_CODES, countryOptions} from '@/lib/countries'
import {useSearchTransition} from './SearchPending'
import type {
  CoverageType,
  Level,
  Locale,
  MajorOption,
  OpportunityKind,
  Region
} from '@/lib/api/types'
import {
  appliesTo,
  countActive,
  EMPTY_FILTERS,
  REGIONS,
  requiresCountry,
  type SearchFilters,
  type SearchMode,
  toSearchParams
} from '@/lib/search/filters'
import './filter-panel.css'

const COVERAGE_TYPES: CoverageType[] = [
  'full_funding',
  'tuition_free',
  'tuition_only',
  'partial',
  'stipend_only'
]

/**
 * Fold a name down to something worth comparing a search term against.
 *
 * Decomposes and strips accents so "Turkiye" finds "Türkiye" and "Ozbekiston"
 * finds "Oʻzbekiston" — a country picker that only matches the exact
 * typography of its own labels is a picker nobody can search.
 */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}]/gu, '')
    .toLowerCase()
}

/**
 * The filter controls.
 *
 * A right-aligned bar above the results from 720px up, and a sheet below it —
 * the same controls either way, so there is one set of inputs to keep working
 * rather than two. Only the layout differs, and it differs in CSS.
 *
 * Filters are held locally while the student is choosing and committed to
 * the URL on "Show results". Pushing on every keystroke would put a server
 * round trip between them and the next checkbox; committing on submit means
 * the URL is always a search someone actually asked for, which is what
 * makes it worth sharing.
 */
export function FilterPanel({
  filters,
  mode,
  locale,
  departments = []
}: {
  filters: SearchFilters
  mode: SearchMode
  locale: Locale
  /**
   * Every department in the catalogue, resolved by the page on the server.
   *
   * The whole list rather than a typeahead: a student filtering a search is
   * picking from what exists, not recalling a name, and 84 options in a
   * native select is a control they already know how to use — typing "mec"
   * jumps to Mechanical Engineering without any code of ours.
   *
   * Passed in rather than fetched here so the options are in the first
   * paint. Fetching on mount would render the select empty, and a shared
   * link carrying `major=mechanical-engineering` would show its raw slug
   * until the request landed.
   */
  departments?: MajorOption[]
}) {
  const t = useTranslations('search')
  const tOpp = useTranslations('opportunity')
  const tUni = useTranslations('university')
  const router = useRouter()
  const pathname = usePathname()
  // The page's shared transition when there is one, so the results can show
  // a loader while the search this panel started is on its way.
  const ownTransition = useTransition()
  const [pending, startTransition] = useSearchTransition() ?? ownTransition
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(filters)
  // What is typed into the number fields, kept as text: binding them to the
  // parsed number made "-5" or "abc" vanish as it was typed, with no word
  // about why.
  const [rawNumbers, setRawNumbers] = useState(() => rawFrom(filters))
  const [countriesOpen, setCountriesOpen] = useState(false)
  const [countryQuery, setCountryQuery] = useState('')
  const countriesLabelId = useId()
  const sheetRef = useRef<HTMLDivElement>(null)
  const openerRef = useRef<HTMLButtonElement>(null)
  const countriesRef = useRef<HTMLDivElement>(null)
  // The chips sit in a row of their own, outside the field, but removing one
  // is still part of choosing countries — it should not close the list.
  const chipsRef = useRef<HTMLUListElement>(null)

  // The URL is the source of truth: a Back button, a cleared filter chip or
  // a shared link all change `filters` under us, and the form has to follow.
  useEffect(() => {
    setDraft(filters)
    setRawNumbers(rawFrom(filters))
  }, [filters])

  // A popover that only closes from its own button is a popover that covers
  // the control the student reached for next.
  useEffect(() => {
    if (!countriesOpen) return
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node
      if (countriesRef.current?.contains(target) || chipsRef.current?.contains(target)) return
      setCountriesOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [countriesOpen])

  useEffect(() => {
    if (!open) return
    const sheet = sheetRef.current
    if (!sheet) return

    const focusable = () =>
      Array.from(
        sheet.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      )
    focusable()[0]?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        close()
        return
      }
      if (event.key !== 'Tab') return
      // A dialog that lets focus walk out behind it is a dialog a keyboard
      // user cannot get back into.
      const items = focusable()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  function close() {
    setOpen(false)
    openerRef.current?.focus()
  }

  function commit(next: SearchFilters) {
    const query = toSearchParams(next, {mode}).toString()
    startTransition(() => {
      // A new search is a new page, not a step in this one: `push` so Back
      // returns to the previous set of results.
      router.push(query ? `${pathname}?${query}` : pathname)
    })
    setOpen(false)
  }

  const numberErrors = {
    maxTuitionUsdYear: parseWhole(rawNumbers.maxTuitionUsdYear, 0) === undefined,
    maxQsRank: parseWhole(rawNumbers.maxQsRank, 1) === undefined
  }

  function setNumber(key: 'maxTuitionUsdYear' | 'maxQsRank', raw: string, min: number) {
    setRawNumbers((current) => ({...current, [key]: raw}))
    const parsed = parseWhole(raw, min)
    if (parsed !== undefined) set(key, parsed)
  }

  function set<K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) {
    setDraft((current) => ({...current, [key]: value}))
  }

  const active = countActive(filters, mode)
  // The university catalogue covers every country QS lists; the other tabs
  // offer the short list the profile asks about.
  const countries = useMemo(
    () => countryOptions(locale, mode === 'universities' ? CATALOGUE_COUNTRY_CODES : undefined),
    [locale, mode]
  )
  // Where a country is required the last one cannot be removed.
  const countryRequired = requiresCountry(mode)
  const selectedCountryNames = draft.countries.map(
    (code) => countries.find((country) => country.code === code)?.name ?? code
  )
  // One list decides what is rendered, counted and sent (`FILTERS_BY_MODE`),
  // so a control can never be on screen without its value reaching the API.
  const shows = (key: keyof SearchFilters) => appliesTo(mode, key)

  const matchingCountries = useMemo(() => {
    const term = fold(countryQuery)
    if (!term) return countries
    // The code as well as the name, so somebody who thinks in "DE" is not
    // made to remember how this app spells Germany.
    return countries.filter(
      (country) => fold(country.name).includes(term) || fold(country.code).startsWith(term)
    )
  }, [countries, countryQuery])

  const departmentOptions = useMemo(() => {
    const options = [
      {value: '', label: t('departmentAny')},
      ...departments.map((department) => ({value: department.slug, label: department.name}))
    ]
    // A slug arriving from a shared link before the catalogue has loaded —
    // or one this catalogue has since dropped — still has to be selectable,
    // or the select would silently show "Any" for a filter that is applied.
    if (draft.major && !departments.some((department) => department.slug === draft.major)) {
      options.push({value: draft.major, label: draft.major})
    }
    return options
  }, [departments, draft.major, t])

  const controls = (
    <form
      className="u-filters__form"
      onSubmit={(event) => {
        event.preventDefault()
        if (
          (shows('maxTuitionUsdYear') && numberErrors.maxTuitionUsdYear) ||
          (shows('maxQsRank') && numberErrors.maxQsRank)
        ) {
          event.currentTarget.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
          return
        }
        commit(draft)
      }}
    >
      {shows('q') ? (
        <Input
          type="search"
          label={t('searchLabel')}
          placeholder={t('searchPlaceholder')}
          value={draft.q}
          maxLength={100}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => set('q', event.target.value)}
        />
      ) : null}

      {shows('kind') ? (
        <Select
          label={t('kind')}
          value={draft.kind ?? ''}
          options={[
            {value: '', label: t('kindAny')},
            {value: 'scholarship', label: t('kindScholarship')},
            {value: 'program', label: t('kindProgram')}
          ]}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
            set('kind', (event.target.value || null) as OpportunityKind | null)
          }
        />
      ) : null}

      {shows('level') ? (
        <Select
          label={t('level')}
          value={draft.level ?? ''}
          options={[
            {value: '', label: t('levelAny')},
            {value: 'bachelor', label: t('levelBachelor')},
            {value: 'master', label: t('levelMaster')}
          ]}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
            set('level', (event.target.value || null) as Level | null)
          }
        />
      ) : null}

      {/* Wrapped rather than given a `className`: `Select` spreads unknown
          props onto the `<select>` after its own `class`, so passing one
          replaces `u-select` and strips the control of every style it has —
          the same trap as `Button` below. The wrapper is the flex item, and
          it is wider than the rest because "Any department" does not fit the
          common width. */}
      {shows('major') ? (
        <div className="u-filters__department">
          <Select
            label={t('department')}
            value={draft.major ?? ''}
            options={departmentOptions}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
              set('major', event.target.value || null)
            }
          />
        </div>
      ) : null}

      {shows('coverageType') ? (
        <Select
          label={t('coverage')}
          value={draft.coverageType ?? ''}
          options={[
            {value: '', label: t('coverageAny')},
            ...COVERAGE_TYPES.map((coverage) => ({
              value: coverage,
              label: tOpp(`coverage_${coverage}`)
            }))
          ]}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
            set('coverageType', (event.target.value || null) as CoverageType | null)
          }
        />
      ) : null}

      {shows('deadlineBefore') ? (
        <Input
          label={t('deadlineBefore')}
          type="date"
          value={draft.deadlineBefore ?? ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            set('deadlineBefore', event.target.value || null)
          }
        />
      ) : null}

      {shows('maxTuitionUsdYear') ? (
        <Input
          label={t('maxTuition')}
          inputMode="numeric"
          hint={t('maxTuitionHelp')}
          error={numberErrors.maxTuitionUsdYear ? t('wholeNumber', {min: 0}) : undefined}
          value={rawNumbers.maxTuitionUsdYear}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setNumber('maxTuitionUsdYear', event.target.value, 0)
          }
        />
      ) : null}

      {shows('maxQsRank') ? (
        <Input
          label={t('maxQsRank')}
          // Text with a numeric keypad rather than `type="number"`: the
          // browser's own number validation refused to submit without
          // saying why, and spinners are fiddly on a phone.
          inputMode="numeric"
          placeholder={t('maxQsRankPlaceholder')}
          hint={t('maxQsRankHelp')}
          error={numberErrors.maxQsRank ? t('wholeNumber', {min: 1}) : undefined}
          value={rawNumbers.maxQsRank}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setNumber('maxQsRank', event.target.value, 1)
          }
        />
      ) : null}

      {/* Wrapped so the bar can place it: beside the countries, to their
          left, since both say where in the world to look. */}
      {shows('region') ? (
        <div className="u-filters__region">
          <Select
            label={t('region')}
            value={draft.region ?? ''}
            options={[
              {value: '', label: t('regionAny')},
              ...REGIONS.map((region) => ({value: region, label: tUni(regionKey(region))}))
            ]}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
              set('region', (event.target.value || null) as Region | null)
            }
          />
        </div>
      ) : null}

      {/* Dozens of checkboxes do not belong in a row of controls, so the
          country list lives behind its own disclosure and carries a search
          box: by the time a list is long enough to need scrolling, reading
          it is slower than typing three letters. The panel is positioned
          over the results in the bar and sits inline in the sheet — same
          markup, different CSS. */}
      <div className="u-filters__countries-field" ref={countriesRef}>
        <span className="u-filters__label" id={countriesLabelId}>
          {t('countries')}
        </span>
        <div className="u-filters__countries-row">
          {/* Styled as the selects beside it, not as a button: it opens a
              list of options, so it should look like the controls that do. */}
          <button
            type="button"
            className="u-select u-filters__countries-trigger"
            aria-expanded={countriesOpen}
            aria-describedby={countriesLabelId}
            onClick={() => setCountriesOpen((current) => !current)}
            data-testid="open-countries"
          >
            {draft.countries.length > 0
              ? t('countriesCount', {count: draft.countries.length})
              : countryRequired
                ? t('countriesNone')
                : t('countriesAny')}
          </button>
        </div>

        {countriesOpen ? (
          <div
            className="u-filters__countries-panel"
            data-testid="countries-panel"
            onKeyDown={(event) => {
              if (event.key !== 'Escape') return
              // Escape closes this panel, not the sheet behind it: the
              // innermost open thing is the one the student meant.
              event.stopPropagation()
              setCountriesOpen(false)
            }}
          >
            <Input
              type="search"
              label={t('countrySearch')}
              placeholder={t('countrySearchPlaceholder')}
              value={countryQuery}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setCountryQuery(event.target.value)
              }
            />
            {matchingCountries.length > 0 ? (
              <div
                className="u-filters__countries"
                role="group"
                aria-labelledby={countriesLabelId}
              >
                {matchingCountries.map((country) => (
                  <Checkbox
                    key={country.code}
                    checked={draft.countries.includes(country.code)}
                    // Where one is required, the last one cannot be unticked —
                    // pick another first.
                    disabled={
                      countryRequired &&
                      draft.countries.length === 1 &&
                      draft.countries[0] === country.code
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      set(
                        'countries',
                        event.target.checked
                          ? [...draft.countries, country.code]
                          : draft.countries.filter((code) => code !== country.code)
                      )
                    }
                  >
                    {country.name}
                  </Checkbox>
                ))}
              </div>
            ) : (
              <p className="u-filters__no-match">{t('countriesNoMatch')}</p>
            )}
          </div>
        ) : null}
      </div>

      {/* Every chosen country, open or closed: the student should see where
          they are searching without opening the list, and a search term
          typed in the panel can filter the ticked boxes out of it. */}
      <ul ref={chipsRef} className="u-filters__chips" hidden={draft.countries.length === 0} data-testid="selected-countries">
        {draft.countries.map((code, index) => (
          <li key={code} className="u-filters__chip">
            <span>{selectedCountryNames[index]}</span>
            {/* Where one is required, the last country cannot be removed —
                pick another first. */}
            {draft.countries.length > 1 || !countryRequired ? (
              <button
                type="button"
                className="u-filters__chip-remove"
                aria-label={t('countryRemove', {country: selectedCountryNames[index]})}
                onClick={() =>
                  set(
                    'countries',
                    draft.countries.filter((current) => current !== code)
                  )
                }
              >
                <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                  <path
                    d="M4.5 4.5l7 7m0-7l-7 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            ) : null}
          </li>
        ))}
      </ul>

      <div className="u-filters__actions">
        <Button type="submit" disabled={pending || (countryRequired && draft.countries.length === 0)}>
          {t('apply')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={pending || active === 0}
          onClick={() => {
            // Where countries are required, clearing keeps them; the sort is
            // an order rather than a filter, so it stays too.
            const cleared = {
              ...EMPTY_FILTERS,
              countries: countryRequired ? filters.countries : [],
              sort: filters.sort
            }
            setDraft(cleared)
            commit(cleared)
          }}
        >
          {t('clear')}
        </Button>
      </div>
    </form>
  )

  return (
    <>
      {/* Wrapped rather than given a `className`: the design system's
          `Button` spreads unknown props onto the element *after* its own
          `class`, so passing one replaces `u-btn` instead of adding to it —
          which quietly strips the button of every style it has. `Card`
          merges; `Button` does not. */}
      <div className="u-filters__opener">
        <Button
          ref={openerRef}
          type="button"
          variant="secondary"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          data-testid="open-filters"
        >
          {active > 0 ? t('filtersWithCount', {count: active}) : t('filters')}
        </Button>
      </div>

      {/* The wide-screen layout: one right-aligned row above the results
          rather than a column beside them, so the list gets the full page
          width. The heading is for screen readers only — sighted users have
          the controls themselves, and a "Filters" title above a single row
          would cost a line of vertical space to say nothing. */}
      <div className="u-filters__bar" data-testid="filter-bar">
        <h2 className="u-filters__heading u-filters__heading--hidden">{t('filters')}</h2>
        {/* One copy of the form at a time: two mounted copies share ids, so
            labels and descriptions could point at the hidden one. */}
        {open ? null : controls}
      </div>

      {open ? (
        <div className="u-filters__backdrop" onClick={close}>
          <div
            ref={sheetRef}
            className="u-filters__sheet"
            role="dialog"
            aria-modal="true"
            aria-label={t('filters')}
            data-testid="filter-sheet"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="u-filters__sheet-head">
              <h2 className="u-filters__heading">{t('filters')}</h2>
              <Button type="button" variant="ghost" size="sm" onClick={close}>
                {t('closeFilters')}
              </Button>
            </div>
            {controls}
          </div>
        </div>
      ) : null}
    </>
  )
}

function rawFrom(filters: SearchFilters) {
  return {
    maxTuitionUsdYear: filters.maxTuitionUsdYear?.toString() ?? '',
    maxQsRank: filters.maxQsRank?.toString() ?? ''
  }
}

/**
 * A whole number at least `min`; `null` for an empty field (no limit);
 * `undefined` for anything else, which the field flags. Spaces and
 * thousands separators are forgiven: "10 000" and "10,000" mean 10000.
 */
function parseWhole(raw: string, min: number): number | null | undefined {
  const cleaned = raw.replace(/[\s,.\u00a0]/g, '')
  if (cleaned === '') return null
  if (!/^\d+$/.test(cleaned)) return undefined
  const value = Number(cleaned)
  return value >= min ? value : undefined
}
